import { createFileRoute } from "@tanstack/react-router";
import React, { useState, useEffect } from "react";
import { useNotificationSettings, NotificationConfig } from "@/hooks/useNotificationSettings";
import apiClient from "@/lib/api-client";
import { toast } from "sonner";
import {
  Bell,
  CreditCard,
  Globe,
  KeyRound,
  Landmark,
  RefreshCw,
  Save,
  ShieldCheck,
  ShieldAlert,
  Trash2,
  UserPlus,
  Wallet,
} from "lucide-react";
import PageHeader from "@/components/shared/PageHeader";
import { Field, PasswordInput } from "@/components/forms";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import PaymentGatewaySettings from "@/components/admin/PaymentGatewaySettings";
import SocialAuthSettings from "@/components/admin/SocialAuthSettings";

export const Route = createFileRoute("/admin/settings/")({
  component: AdminSettingsPage,
});

const SETTINGS_TABS = [
  { label: "General", icon: <Globe /> },
  { label: "Pricing & Tiers", icon: <Wallet /> },
  { label: "Security & Infrastructure", icon: <ShieldCheck /> },
  { label: "Notifications", icon: <Bell /> },
  { label: "Data Sync", icon: <RefreshCw /> },
  { label: "Payments", icon: <CreditCard /> },
  { label: "Sign-in providers", icon: <KeyRound /> },
];

function AdminSettingsPage() {
  const [tab, setTab] = useState(0);
  const [saved, setSaved] = useState(false);
  const { config, updateConfig } = useNotificationSettings();

  const [formConfig, setFormConfig] = useState<NotificationConfig>({
    smtp_host: "",
    smtp_port: 587,
    smtp_user: "",
    smtp_pass: "",
    smtp_sender: "",
    vapid_public_key: "",
    vapid_private_key: "",
    web_push_enabled: false,
    email_enabled: false,
  });

  const [settings, setSettings] = useState({
    siteName: "",
    siteEmail: "",
    maintenanceMode: false,
    allowRegistration: true,
    requireEmailVerification: true,
    enableNotifications: true,
    maxFileSize: "10",
    timezone: "Africa/Dar_es_Salaam",
    language: "en",
    verifiedPrice: "",
    premiumPrice: "",
    verifiedLabel: "",
    premiumLabel: "",
  });

  const [originalSettings, setOriginalSettings] = useState(settings);
  const [originalConfig, setOriginalConfig] = useState<NotificationConfig | null>(null);

  useEffect(() => {
    apiClient
      .get("/settings/pricing")
      .then((res) => {
        const data = res.data.data;
        const pricingUpdate = {
          verifiedPrice: data.verified_price || "",
          premiumPrice: data.premium_price || "",
          verifiedLabel: data.verified_label || "",
          premiumLabel: data.premium_label || "",
        };
        setSettings((prev) => ({ ...prev, ...pricingUpdate }));
        setOriginalSettings((prev) => ({ ...prev, ...pricingUpdate }));
      })
      .catch(() => {});
    apiClient
      .get("/admin/settings")
      .then((res) => {
        const data = res.data.data;
        if (data) {
          const generalUpdate = {
            siteName: data.site_name || "",
            siteEmail: data.admin_email || "",
            timezone: data.timezone || "Africa/Dar_es_Salaam",
            maintenanceMode: data.maintenance_mode === "true" || data.maintenance_mode === true,
            allowRegistration: data.allow_registration !== "false" && data.allow_registration !== false,
          };
          setSettings((prev) => ({ ...prev, ...generalUpdate }));
          setOriginalSettings((prev) => ({ ...prev, ...generalUpdate }));
        }
      })
      .catch(() => {});
    if (config) {
      setFormConfig(config);
      setOriginalConfig(config);
    }
  }, [config]);

  const handleSave = async () => {
    try {
      if (originalConfig) {
        const changedNotifFields: Partial<NotificationConfig> = {};
        for (const key of Object.keys(formConfig) as (keyof NotificationConfig)[]) {
          if (formConfig[key] !== originalConfig[key]) {
            (changedNotifFields as any)[key] = formConfig[key];
          }
        }
        if (Object.keys(changedNotifFields).length > 0) {
          await updateConfig.mutateAsync({ ...originalConfig, ...changedNotifFields } as NotificationConfig);
        }
      } else {
        await updateConfig.mutateAsync(formConfig);
      }

      const generalPayload: Record<string, any> = {};
      if (settings.siteName !== originalSettings.siteName) generalPayload.site_name = settings.siteName;
      if (settings.siteEmail !== originalSettings.siteEmail) generalPayload.admin_email = settings.siteEmail;
      if (settings.timezone !== originalSettings.timezone) generalPayload.timezone = settings.timezone;
      if (settings.maintenanceMode !== originalSettings.maintenanceMode) generalPayload.maintenance_mode = settings.maintenanceMode;
      if (settings.allowRegistration !== originalSettings.allowRegistration) generalPayload.allow_registration = settings.allowRegistration;
      if (settings.verifiedPrice !== originalSettings.verifiedPrice) generalPayload.verified_price = settings.verifiedPrice;
      if (settings.premiumPrice !== originalSettings.premiumPrice) generalPayload.premium_price = settings.premiumPrice;
      if (settings.verifiedLabel !== originalSettings.verifiedLabel) generalPayload.verified_label = settings.verifiedLabel;
      if (settings.premiumLabel !== originalSettings.premiumLabel) generalPayload.premium_label = settings.premiumLabel;

      if (Object.keys(generalPayload).length > 0) {
        await apiClient.put("/admin/settings", generalPayload);
      }

      toast.success("System configuration deployed.");
      setOriginalSettings(settings);
      setOriginalConfig(formConfig);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      toast.error("Deployment failed.");
    }
  };


  return (
    <div className="space-y-6">
      <PageHeader
        title="Command center"
        subtitle="Global platform orchestration and security parameters."
        breadcrumbs={[{ label: "Admin", path: "/admin/dashboard" }, { label: "Settings" }]}
        action={
          <Button onClick={handleSave}>
            <Save />
            Deploy changes
          </Button>
        }
      />

      <Tabs
        value={String(tab)}
        onValueChange={(v) => setTab(Number(v))}
        orientation="vertical"
        className="grid gap-6 lg:grid-cols-[248px_minmax(0,1fr)] lg:gap-8"
      >
        {/* Section navigation */}
        <TabsList
          aria-label="Settings sections"
          className="h-auto gap-2 lg:sticky lg:top-0 lg:flex-col lg:items-stretch lg:gap-1.5 lg:self-start lg:rounded-2xl lg:border lg:border-border lg:bg-card lg:p-2 lg:shadow-soft"
        >
          {SETTINGS_TABS.map((t, i) => (
            <TabsTrigger
              key={t.label}
              value={String(i)}
              className="lg:h-11 lg:justify-start lg:gap-3 lg:bg-transparent lg:px-4 lg:text-sm lg:hover:bg-muted lg:data-[state=active]:bg-primary lg:data-[state=active]:hover:bg-primary"
            >
              {t.icon}
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* Content */}
        <div className="min-w-0 rounded-2xl border border-border bg-card p-5 shadow-soft md:p-7">
          {/* TAB 0: GENERAL */}
          <TabsContent value="0" className="mt-0 space-y-8">
            <SettingsSection
              title="Global identity"
              description="Configure how the platform identifies itself to users and search engines."
            >
              <div className="grid gap-4 md:grid-cols-2">
                <Field label="Organization name">
                  <Input
                    value={settings.siteName}
                    onChange={(e) => setSettings({ ...settings, siteName: e.target.value })}
                  />
                </Field>
                <Field label="Admin email">
                  <Input
                    value={settings.siteEmail}
                    onChange={(e) => setSettings({ ...settings, siteEmail: e.target.value })}
                  />
                </Field>
                <Field label="Timezone">
                  {({ id }) => (
                    <Select
                      value={settings.timezone}
                      onValueChange={(v) => setSettings({ ...settings, timezone: v })}
                    >
                      <SelectTrigger id={id}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Africa/Dar_es_Salaam">East Africa (EAT)</SelectItem>
                        <SelectItem value="UTC">Universal (UTC)</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </Field>
              </div>
            </SettingsSection>
            <Separator />
            <SettingsSection title="Operational guardrails">
              <div className="divide-y divide-border rounded-md border border-border">
                <ToggleRow
                  icon={<ShieldAlert />}
                  title="Maintenance mode"
                  desc="Lock platform access for all non-admin users."
                  checked={settings.maintenanceMode}
                  onChange={(v: boolean) => setSettings({ ...settings, maintenanceMode: v })}
                />
                <ToggleRow
                  icon={<UserPlus />}
                  title="Open enrollment"
                  desc="Allow new student and partner registrations."
                  checked={settings.allowRegistration}
                  onChange={(v: boolean) => setSettings({ ...settings, allowRegistration: v })}
                />
              </div>
            </SettingsSection>
          </TabsContent>

          {/* TAB 1: PRICING */}
          <TabsContent value="1" className="mt-0">
            <SettingsSection
              title="Tier economics"
              description="Manage pricing strategies for student membership levels."
            >
              <div className="grid gap-4 md:grid-cols-2">
                <Field label="Verified price (TZS)">
                  <Input
                    type="number"
                    className="font-mono tabular-nums"
                    value={settings.verifiedPrice}
                    onChange={(e) => setSettings({ ...settings, verifiedPrice: e.target.value })}
                  />
                </Field>
                <Field label="Verified label">
                  <Input
                    value={settings.verifiedLabel}
                    onChange={(e) => setSettings({ ...settings, verifiedLabel: e.target.value })}
                  />
                </Field>
                <Field label="Premium price (TZS)">
                  <Input
                    type="number"
                    className="font-mono tabular-nums"
                    value={settings.premiumPrice}
                    onChange={(e) => setSettings({ ...settings, premiumPrice: e.target.value })}
                  />
                </Field>
                <Field label="Premium label">
                  <Input
                    value={settings.premiumLabel}
                    onChange={(e) => setSettings({ ...settings, premiumLabel: e.target.value })}
                  />
                </Field>
              </div>
            </SettingsSection>
          </TabsContent>

          {/* TAB 2: SECURITY & INFRA */}
          <TabsContent value="2" className="mt-0 space-y-8">
            <SettingsSection title="Infrastructure metrics">
              <div className="rounded-md border border-border bg-background p-4">
                <p className="text-sm font-medium text-foreground">Cloud storage usage</p>
                <p className="text-caption text-muted-foreground">
                  Storage data is provided by the platform infrastructure.
                </p>
                <p className="mt-3 text-sm text-body">
                  Connect an infrastructure monitoring endpoint to display real-time storage usage.
                </p>
              </div>
            </SettingsSection>
            <SettingsSection title="System cleanup">
              <div className="flex flex-col gap-2 sm:flex-row">
                <Button variant="destructive-outline">
                  <Trash2 />
                  Flush system cache
                </Button>
                <Button variant="outline">
                  <KeyRound />
                  Rotate API keys
                </Button>
              </div>
            </SettingsSection>
          </TabsContent>

          {/* TAB 3: NOTIFICATIONS */}
          <TabsContent value="3" className="mt-0">
            <div className="grid gap-8 md:grid-cols-2">
              <SettingsSection title="SMTP configuration">
                <div className="space-y-4">
                  <Field label="SMTP host">
                    <Input
                      value={formConfig.smtp_host}
                      onChange={(e) => setFormConfig({ ...formConfig, smtp_host: e.target.value })}
                    />
                  </Field>
                  <Field label="SMTP port">
                    <Input
                      type="number"
                      className="font-mono tabular-nums"
                      value={formConfig.smtp_port}
                      onChange={(e) =>
                        setFormConfig({ ...formConfig, smtp_port: parseInt(e.target.value) })
                      }
                    />
                  </Field>
                  <Field label="Username">
                    <Input
                      value={formConfig.smtp_user}
                      onChange={(e) => setFormConfig({ ...formConfig, smtp_user: e.target.value })}
                    />
                  </Field>
                  <Field label="Password">
                    <PasswordInput
                      autoComplete="new-password"
                      value={formConfig.smtp_pass}
                      onChange={(e) => setFormConfig({ ...formConfig, smtp_pass: e.target.value })}
                    />
                  </Field>
                </div>
              </SettingsSection>
              <SettingsSection title="Web push (VAPID)">
                <div className="space-y-4">
                  <Field label="Public key">
                    <Textarea
                      rows={2}
                      className="min-h-0 font-mono text-[13px]"
                      value={formConfig.vapid_public_key}
                      onChange={(e) =>
                        setFormConfig({ ...formConfig, vapid_public_key: e.target.value })
                      }
                    />
                  </Field>
                  <Field label="Private key">
                    <Textarea
                      rows={2}
                      className="min-h-0 font-mono text-[13px]"
                      value={formConfig.vapid_private_key}
                      onChange={(e) =>
                        setFormConfig({ ...formConfig, vapid_private_key: e.target.value })
                      }
                    />
                  </Field>
                  <p className="rounded-md border border-dashed border-border p-3 text-caption text-muted-foreground">
                    VAPID keys are essential for browser push notifications without a middleman
                    service.
                  </p>
                </div>
              </SettingsSection>
            </div>
          </TabsContent>

          {/* TAB 4: DATA SYNC */}
          <TabsContent value="4" className="mt-0">
            <SettingsSection title="Institutional scrapers">
              <div className="grid gap-4 md:grid-cols-2">
                <SyncCard
                  title="TCU Registry"
                  desc="Sync all accredited Tanzanian Universities and degree programs."
                  onSync={async () => {
                    try {
                      await apiClient.post("/tcu/sync/tcu");
                      toast.success("TCU registry sync initiated in background.");
                    } catch (err) {
                      toast.error("Failed to start TCU sync.");
                    }
                  }}
                />
                <SyncCard
                  title="NACTVET Registry"
                  desc="Sync technical colleges and diploma-level institutions."
                  onSync={async () => {
                    try {
                      await apiClient.post("/tcu/sync/nactvet");
                      toast.success("NACTVET registry sync initiated in background.");
                    } catch (err) {
                      toast.error("Failed to start NACTVET sync.");
                    }
                  }}
                />
              </div>
            </SettingsSection>
          </TabsContent>

          {/* TAB 5: PAYMENTS. Saved and tested on its own, not by Deploy Changes. */}
          <TabsContent value="5" className="mt-0">
            <PaymentGatewaySettings />
          </TabsContent>

          {/* TAB 6: SIGN-IN PROVIDERS. Saved on its own, not by Deploy Changes. */}
          <TabsContent value="6" className="mt-0">
            <SocialAuthSettings />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}

// --- Sub-components ---

function SettingsSection({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-[15px] leading-6 font-medium text-foreground">{title}</h2>
        {description && <p className="text-sm text-muted-foreground">{description}</p>}
      </div>
      {children}
    </section>
  );
}

function ToggleRow({ icon, title, desc, checked, onChange }: any) {
  const id = React.useId();
  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <span
        className="flex size-8 shrink-0 items-center justify-center rounded-sm border border-border bg-background text-body [&_svg]:size-4"
        aria-hidden
      >
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        <label htmlFor={id} className="block text-sm font-medium text-foreground">
          {title}
        </label>
        <p id={`${id}-desc`} className="text-caption text-muted-foreground">
          {desc}
        </p>
      </div>
      <Switch
        id={id}
        aria-describedby={`${id}-desc`}
        checked={checked}
        onCheckedChange={(v) => onChange(v)}
      />
    </div>
  );
}

function SyncCard({ title, desc, onSync }: any) {
  return (
    <div className="flex flex-col rounded-md border border-border bg-background p-4">
      <div className="mb-1 flex items-center gap-2">
        <Landmark className="size-4 text-muted-foreground" aria-hidden />
        <h3 className="text-sm font-medium text-foreground">{title}</h3>
      </div>
      <p className="mb-4 flex-1 text-sm text-muted-foreground">{desc}</p>
      <Button variant="outline" className="w-full" onClick={onSync}>
        <RefreshCw />
        Initialize sync
      </Button>
    </div>
  );
}

export default AdminSettingsPage;
